/**
 * PasscodeGate — full-screen access wall for the main app.
 *
 * If ACCESS_PASSCODE is set in the server environment, /api/config returns
 * a SHA-256 hash in the opaque "ac" field. This component renders a
 * passcode prompt and compares the entered value against that hash.
 *
 * The raw passcode never leaves the server. The hash is safe to expose
 * publicly — SHA-256 of a short alphanumeric code is not a password
 * vault, it's a lightweight deterrent against bots and casual browsing.
 *
 * The unlocked state lives in React memory only — a page refresh re-prompts.
 * This is intentional: it means shared/cached URLs don't stay unlocked.
 */

import { useState, useEffect, useRef, type ReactNode } from "react";

// Web Crypto SHA-256 — available in all modern browsers, no deps needed
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

interface PasscodeGateProps {
  children: ReactNode;
}

type GateState = "loading" | "unlocked" | "locked" | "error";

export function PasscodeGate({ children }: PasscodeGateProps) {
  const [gateState, setGateState] = useState<GateState>("loading");
  const [expectedHash, setExpectedHash] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch("/api/config")
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((cfg: Record<string, unknown>) => {
        const hash = cfg["ac"] as string | null;
        if (!hash) {
          // No passcode configured — open access
          setGateState("unlocked");
        } else {
          setExpectedHash(hash);
          setGateState("locked");
          // Focus input after render
          setTimeout(() => inputRef.current?.focus(), 50);
        }
      })
      .catch(() => {
        // Config fetch failed — fail closed rather than exposing the app
        setGateState("error");
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !expectedHash) return;
    setChecking(true);

    const entered = input.trim().toUpperCase();
    const hash = await sha256(entered);

    setChecking(false);

    if (hash === expectedHash) {
      setGateState("unlocked");
    } else {
      // Wrong — shake and clear
      setShake(true);
      setInput("");
      setTimeout(() => {
        setShake(false);
        inputRef.current?.focus();
      }, 600);
    }
  };

  // Pass-through states
  if (gateState === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (gateState === "error") {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <p className="text-red-400 text-sm font-medium mb-2">Unable to verify access</p>
        <p className="text-slate-500 text-xs">Check your connection and refresh the page.</p>
      </div>
    );
  }

  if (gateState === "unlocked") {
    return <>{children}</>;
  }

  // Locked — show full-screen gate
  return (
    <div data-testid="access-gate" className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
      {/* Logo mark */}
      <div className="mb-8 flex flex-col items-center gap-3">
        {/* Logo mark — shield + tick, BS 5499 emergency-exit green */}
        <svg
          viewBox="0 0 32 32"
          className="w-12 h-12"
          fill="none"
          aria-label="FireSafetyOps"
        >
          <polygon points="4,5 28,5 28,18 16,30 4,18" fill="#00843D" />
          <polygon points="10,17 14.5,21.5 22,12 19.5,9.8 14.2,16.2 12,14" fill="#ffffff" />
        </svg>
        <span className="text-white text-xl font-semibold tracking-wide">FireSafetyOps</span>
      </div>

      {/* Card */}
      <div
        className={`w-full max-w-sm bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl transition-transform ${
          shake ? "animate-[shake_0.5s_ease-in-out]" : ""
        }`}
        style={shake ? { animation: "shake 0.5s ease-in-out" } : {}}
      >
        <h1 className="text-white text-lg font-semibold text-center mb-1">Access required</h1>
        <p className="text-slate-400 text-sm text-center mb-6">
          Enter the access code to continue
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
            placeholder="ACCESS CODE"
            maxLength={20}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            data-testid="access-code-input"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white text-center text-lg tracking-[0.25em] font-mono placeholder:text-slate-600 placeholder:tracking-normal placeholder:font-sans focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
          />
          <button
            type="submit"
            disabled={checking || !input.trim()}
            data-testid="access-code-submit"
            className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors"
          >
            {checking ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>

      {/* Shake keyframe — injected inline to avoid a stylesheet dep */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-8px); }
          30%       { transform: translateX(8px); }
          45%       { transform: translateX(-6px); }
          60%       { transform: translateX(6px); }
          75%       { transform: translateX(-4px); }
          90%       { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
